import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ChevronRight, Copy, Link } from 'lucide-react';
import { Button, Loader } from '@getro/rombo';
import { useCalibrationSelection } from '../hooks/useCalibrationSelection';

export const CalibrationSelection = ({
  jobTitle,
  userFullName,
  onStartCalibration,
  isLoading,
  job,
  collectionId,
  jobId,
}) => {
  const { handleCopyMessage, handleCopyLink, handleOpenCalibrationLink, messageWithFullLink, isLoadingToken } =
    useCalibrationSelection({
      jobTitle,
      userFullName,
      collectionId,
      jobId,
      job,
    });

  // Parse message to render link as clickable
  const renderMessageWithLink = () => {
    // Match any URL in the message
    const urlRegex = /(https?:\/\/[^\s]+)/;
    const match = messageWithFullLink.match(urlRegex);

    if (match) {
      const linkUrl = match[0];
      const parts = messageWithFullLink.split(linkUrl);
      const [beforeLink, afterLink] = parts;

      return (
        <>
          <Text as="span" fontSize="14px" color="text.main">
            {beforeLink}
          </Text>
          <Text
            as="span"
            fontSize="13px"
            onClick={handleOpenCalibrationLink}
            sx={{
              color: 'primary.500',
              textDecoration: 'underline',
              cursor: 'pointer',
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              '&:hover': {
                color: 'primary.600',
              },
            }}
          >
            {linkUrl}
          </Text>
          <Text as="span" fontSize="14px" color="text.main">
            {afterLink}
          </Text>
        </>
      );
    }
    return (
      <Text fontSize="14px" color="text.main" sx={{ whiteSpace: 'pre-wrap', lineHeight: '20px' }}>
        {messageWithFullLink}
      </Text>
    );
  };

  if (isLoadingToken) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          width: '100%',
        }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ gap: '48px', display: 'flex', flexDirection: 'column', width: '100%', lineHeight: '1.4' }}>
      <Box sx={{ gap: '8px', display: 'flex', flexDirection: 'column' }}>
        <Text
          fontSize="12px"
          fontWeight="regular"
          color="text.subtle"
          sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}
        >
          Step 1 of 2: Calibration
        </Text>
        <Text fontSize="20px" fontWeight="semibold" color="text.dark">
          Who will calibrate?
        </Text>
        <Text fontSize="14px" color="text.main">
          Improve outcomes by helping the agent learn what &quot;great&quot; candidates look like.
        </Text>
      </Box>

      <Box sx={{ gap: '32px', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ gap: '8px', display: 'flex', flexDirection: 'column' }}>
          <Text fontSize="14px" fontWeight="medium" color="text.dark">
            I&apos;ll calibrate myself
          </Text>
          <Text fontSize="14px" color="text.main">
            Just a quick chat with the agent (about 3 minutes), to optimize results.
          </Text>
          <Button
            variant="primary"
            onClick={onStartCalibration}
            loading={isLoading}
            disabled={isLoading}
            icon={ChevronRight}
            iconPosition="right"
            size="small"
            iconGap="8px"
            sx={{ alignSelf: 'flex-start' }}
          >
            {job?.sourcingCampaign?.icpId ? 'Continue calibration' : 'Start calibration'}
          </Button>
        </Box>

        <Box
          sx={{
            height: '1px',
            bg: 'border.subtle',
            width: '100%',
          }}
        />

        <Box sx={{ gap: '8px', display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Text fontSize="14px" fontWeight="medium" color="text.dark">
            The hiring manager
          </Text>
          <Text fontSize="14px" color="text.main">
            Share this message (or just the link below) and let the hiring manager handle calibration, no account
            needed. We&apos;ll email you when it&apos;s done.
          </Text>

          <Box
            sx={{
              bg: 'neutral.20',
              borderRadius: '4px',
              p: '16px',
              gap: '16px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              fontSize="14px"
              color="text.main"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: '20px',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {renderMessageWithLink()}
            </Box>
            <Flex sx={{ gap: '8px' }}>
              <Button variant="primary" onClick={handleCopyMessage} icon={Copy} iconGap="8px" size="small">
                Copy message
              </Button>
              <Button variant="secondary" onClick={handleCopyLink} icon={Link} iconGap="8px" size="small">
                Copy link
              </Button>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

CalibrationSelection.propTypes = {
  jobTitle: PropTypes.string.isRequired,
  userFullName: PropTypes.string.isRequired,
  onStartCalibration: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  job: PropTypes.shape({
    slug: PropTypes.string,
    sourcingCampaign: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      icpId: PropTypes.string,
    }),
  }).isRequired,
  collectionId: PropTypes.number,
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CalibrationSelection.defaultProps = {
  collectionId: null,
  jobId: null,
};
